import React, { useState } from "react";
import {
  Box,
  Button,
  Input,
  Text,
  Heading,
  VStack,
} from "@chakra-ui/react";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { cognito } from "../cognito";
import logo from "../assets/logo.png"; // ✅ logo import

const client = new CognitoIdentityProviderClient({ region: cognito.region });

// --- Password validator
function validatePassword(pw: string): string | null {
  if (pw.length < 8) return "Password must be at least 8 characters long";
  if (!/[A-Z]/.test(pw)) return "Password must include at least one uppercase letter";
  if (!/[a-z]/.test(pw)) return "Password must include at least one lowercase letter";
  if (!/[0-9]/.test(pw)) return "Password must include at least one number";
  if (!/[!@#$%^&*(),.?\":{}|<>]/.test(pw))
    return "Password must include at least one special character";
  return null;
}

export default function CustomLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [step, setStep] = useState<"login" | "newPassword" | "forgotPassword" | "resetPassword">("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const navigate = useNavigate();
  const { login } = useAuth();
  const [session, setSession] = useState<string>();

  // --- Login submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: cognito.userPoolClientId,
        AuthParameters: {
          USERNAME: username.trim().toLowerCase(),
          PASSWORD: password,
        },
      });

      const res = await client.send(command);
      if (res.AuthenticationResult) {
        login({
          idToken: res.AuthenticationResult.IdToken!,
          accessToken: res.AuthenticationResult.AccessToken!,
          refreshToken: res.AuthenticationResult.RefreshToken!,
          email: username,
        });
        navigate("/");
      } else if (res.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        setStep("newPassword");
        setSession(res.Session);
      }
    } catch (err: any) {
      setError(err.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  // --- Handle new password required
  const handleNewPassword = async () => {
    if (!session) return;
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: cognito.userPoolClientId,
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
        },
      });
      const res = await client.send(command);
      if (res.AuthenticationResult) {
        login({
          idToken: res.AuthenticationResult.IdToken!,
          accessToken: res.AuthenticationResult.AccessToken!,
          refreshToken: res.AuthenticationResult.RefreshToken!,
          email: username,
        });
        navigate("/");
      }
    } catch (err: any) {
      setError(err.message || "Failed to set new password");
    } finally {
      setLoading(false);
    }
  };

  // --- Forgot password request
  const handleForgotPassword = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const command = new ForgotPasswordCommand({
        ClientId: cognito.userPoolClientId,
        Username: username.trim().toLowerCase(),
      });
      await client.send(command);
      setStep("resetPassword");
      setMessage("Verification code sent to your email/phone.");
    } catch (err: any) {
      setError(err.message || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  // --- Confirm new password with reset code
  const handleResetPassword = async () => {
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");
    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: cognito.userPoolClientId,
        Username: username,
        ConfirmationCode: resetCode,
        Password: newPassword,
      });
      await client.send(command);
      setMessage("Password reset successful. You can now log in.");
      setStep("login");
    } catch (err: any) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDir="column" alignItems="center" justifyContent="center" bg="brand.900">
      {/* Logo above login box */}
      <Box mb={8}>
        <img src={logo} alt="Logo" style={{ width: 200, height: "auto" }} />
      </Box>

      {/* Login / reset / password box */}
      <Box
        maxW="sm"
        w="full"
        p={8}
        borderWidth={1}
        borderRadius="lg"
        borderColor="yellow.600"
        bg="brand.800"
        color="brand.yellow"
      >
        <Heading mb={6} textAlign="center">
          Sign In
        </Heading>

        {error && <Text color="red.400" mb={4} textAlign="center">{error}</Text>}
        {message && <Text color="green.400" mb={4} textAlign="center">{message}</Text>}

        {/* --- Login Step */}
        {step === "login" && (
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <Input placeholder="Email" value={username} onChange={(e) => setUsername(e.target.value)} />
              <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <Button type="submit" colorScheme="yellow" w="full" isLoading={loading}>
                Sign In
              </Button>
              <Button variant="link" color="yellow.300" onClick={() => setStep("forgotPassword")}>
                Forgot your password?
              </Button>
            </VStack>
          </form>
        )}

        {/* --- New Password Required */}
        {step === "newPassword" && (
          <VStack spacing={4}>
            <Input type="password" placeholder="Enter new password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <Button colorScheme="yellow" w="full" onClick={handleNewPassword} isLoading={loading}>
              Set New Password
            </Button>
          </VStack>
        )}

        {/* --- Forgot Password Request */}
        {step === "forgotPassword" && (
          <VStack spacing={4}>
            <Input placeholder="Enter your email" value={username} onChange={(e) => setUsername(e.target.value)} />
            <Button colorScheme="yellow" w="full" onClick={handleForgotPassword} isLoading={loading}>
              Send Reset Code
            </Button>
            <Button variant="link" color="yellow.300" onClick={() => setStep("login")}>
              Back to Login
            </Button>
          </VStack>
        )}

        {/* --- Reset Password with Code */}
        {step === "resetPassword" && (
          <VStack spacing={4}>
            <Input placeholder="Verification code" value={resetCode} onChange={(e) => setResetCode(e.target.value)} />
            <Input type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <Button colorScheme="yellow" w="full" onClick={handleResetPassword} isLoading={loading}>
              Reset Password
            </Button>
          </VStack>
        )}

      </Box>
    </Box>
  );
}
